import React, { useState, useEffect } from 'react';
import { Button, InputNumber, message, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useQuote } from '../context/QuoteContext';
import { submitCalibration } from '../api';

export default function Result() {
  const { state, dispatch } = useQuote();
  const navigate = useNavigate();
  const quotes = state.quotes || [];
  const parts = state.parts || [];

  const [actualPrices, setActualPrices] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [selectedLeadType, setSelectedLeadType] = useState('standard');
  const [selectedCards, setSelectedCards] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPartIndex, setEditingPartIndex] = useState(null);

  useEffect(() => {
    if (quotes.length === 0) {
      navigate('/upload');
    }
  }, [quotes.length, navigate]);

  if (quotes.length === 0) return null;

  // ================= 数据处理 =================
  const getBasePrice = (quote) => {
    // 兼容不同的返回结构
    if (quote.quoteResult?.breakdown) return quote.quoteResult.breakdown.total_price;
    if (quote.quoteResult?.total_price) return quote.quoteResult.total_price;
    return 0;
  };

  const totalBasePrice = quotes.reduce((sum, q) => sum + getBasePrice(q), 0);
  
  // 交期与价格联动
  const leadMultipliers = { economy: 0.8, standard: 1.0, expedited: 1.2 };
  const leadDaysRange = { economy: '10-12 天', standard: '6-8 天', expedited: '4-5 天' };
  const currentTotalPrice = totalBasePrice * leadMultipliers[selectedLeadType];

  // ================= 交互逻辑 =================
  const handleCalibrate = async (index) => {
    const actualPrice = actualPrices[index];
    if (!actualPrice || actualPrice <= 0) {
      message.warning('请输入有效的实际成交价');
      return;
    }
    setSubmitting(true);
    const quote = quotes[index];
    const part = parts[index];
    try {
      await submitCalibration({
        task_id: part.taskId || 'unknown',
        part_name: part.cadFile?.name || `零件 ${index + 1}`,
        material_category: part.material?.category || '',
        material_name: part.material?.name || '',
        quantity: part.quantity || 1,
        volume_cm3: quote.quoteResult?.volume_cm3 || 0,
        difficulty_coefficient: quote.quoteResult?.breakdown?.difficulty_coefficient || 1.0,
        recommended_machine: quote.quoteResult?.recommended_machine || '3-axis mill',
        system_price: getBasePrice(quote),
        actual_price: actualPrice,
        tolerance_grade: part.toleranceGrade || '未标注/自由公差',
        roughness_ra: part.roughnessRa || 3.2,
      });
      message.success(`零件 ${index + 1} 校准数据已提交`);
      setActualPrices(prev => ({ ...prev, [index]: null }));
    } catch (err) {
      message.error('校准提交失败');
    }
    setSubmitting(false);
  };

  const handleSelectCard = (index, checked) => {
    if (checked) setSelectedCards(prev => [...prev, index]);
    else setSelectedCards(prev => prev.filter(i => i !== index));
  };

  const handleSelectAll = () => {
    if (selectedCards.length === quotes.length) setSelectedCards([]);
    else setSelectedCards(quotes.map((_, i) => i));
  };

  const openEditModal = (idx) => {
    setEditingPartIndex(idx);
    setModalVisible(true);
  };

  // ================= UI 渲染 =================
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#000814', color: '#e0e0e0', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      
      {/* ================= 左侧导航 ================= */}
      <div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: 210, background: 'rgba(0,10,30,0.95)', borderRight: '1px solid rgba(0,229,255,0.15)', padding: '20px 14px', display: 'flex', flexDirection: 'column', zIndex: 100 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#00e5ff', letterSpacing: 1, marginBottom: 32 }}>PROLEAN</div>
        <div onClick={() => navigate('/')} style={{ color: '#fff', fontSize: 16, padding: '10px 8px', cursor: 'pointer', borderRadius: 6, marginBottom: 4 }}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#00e5ff', marginRight: 8 }}></span>New quote
          <span style={{ fontSize: 13, color: '#fff', marginLeft: 16, display: 'block' }}>新报价</span>
        </div>
        <div style={{ color: '#fff', fontSize: 16, padding: '10px 8px', cursor: 'pointer', borderRadius: 6, marginBottom: 4 }}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#556', marginRight: 8 }}></span>Sign in
          <span style={{ fontSize: 13, color: '#fff', marginLeft: 16, display: 'block' }}>登录</span>
        </div>
        <div style={{ color: '#fff', fontSize: 16, padding: '10px 8px', cursor: 'pointer', borderRadius: 6, marginBottom: 4 }}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#556', marginRight: 8 }}></span>Help
          <span style={{ fontSize: 13, color: '#fff', marginLeft: 16, display: 'block' }}>帮助</span>
        </div>
        <div style={{ marginTop: 'auto', color: '#667', fontSize: 14, padding: '10px 8px', cursor: 'pointer' }}>English</div>
      </div>

      {/* ================= 中间主内容 ================= */}
      <div style={{ marginLeft: 210, marginRight: 320, padding: '30px 40px', flex: 1 }}>
        
        {/* 顶部操作栏 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fff' }}>
            报价结果 <span style={{ fontSize: 14, color: '#00e5ff', marginLeft: 10, fontWeight: 'normal' }}>Quote Results</span>
          </div>
          <div style={{ display: 'flex', gap: 15, alignItems: 'center', fontSize: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 8, color: '#8899aa' }}>
              <input type="checkbox" checked={selectedCards.length === quotes.length && quotes.length > 0} onChange={handleSelectAll} style={{ accentColor: '#00e5ff', width: 16, height: 16, cursor: 'pointer' }} /> 
              全选 Select All
            </label>
            <Button 
              disabled={selectedCards.length === 0}
              onClick={() => navigate('/parameters')}
              style={{ background: 'rgba(0,229,255,0.1)', border: '1px solid #00e5ff', color: '#00e5ff', borderRadius: 6 }}>
              批量设置选中零件 Batch Set
            </Button>
          </div>
        </div>

        {/* 循环渲染卡片 */}
        {quotes.map((q, idx) => {
          const part = parts[idx] || {};
          const result = part.result || {};
          const features = result.features || {};
          const isSelected = selectedCards.includes(idx);
          
          const basePrice = getBasePrice(q);
          const qty = part.quantity || 1;
          const unitPrice = basePrice / qty;
          
          // 尺寸和体积换算
          const dims = result.dimensions_mm || [0, 0, 0];
          const vol = result.volume_cm3 || 0;
          const dimsStr = dims.map(d => d.toFixed(1)).join(' × ');
          const dimsInStr = dims.map(d => (d / 25.4).toFixed(2)).join(' × ');
          const volIn = (vol / 16387.064).toFixed(2); // cm3 to cubic inches
          
          // 螺纹判断
          const hasThreads = features.threaded_holes > 0;
          const threadDetails = features.thread_details || [];
          const threadStr = hasThreads 
            ? threadDetails.map(t => `${t.designation}×${t.quantity}`).join(', ') || `${features.threaded_holes} holes`
            : 'No threads';

          return (
            <div key={idx} 
              style={{ 
                background: isSelected ? 'rgba(0, 30, 60, 0.9)' : 'rgba(0, 20, 40, 0.85)', 
                border: `1px solid ${isSelected ? '#00e5ff' : 'rgba(0, 229, 255, 0.3)'}`, 
                borderRadius: 12, 
                boxShadow: isSelected ? '0 0 20px rgba(0,229,255,0.2)' : '0 0 30px rgba(0, 229, 255, 0.08)', 
                display: 'flex', 
                padding: 30, 
                gap: 40, 
                marginBottom: 24, 
                backdropFilter: 'blur(10px)' 
              }}>
              
              {/* 左侧：3D图 */}
              <div style={{ flex: '0 0 220px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ width: '100%', height: 160, background: 'linear-gradient(135deg, #0a1628, #0d1f2e, #0a1a1a)', border: '1px solid rgba(0, 229, 255, 0.2)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00e5ff', fontSize: 13, marginBottom: 15 }}>
                  3D Preview
                </div>
                <a onClick={() => navigate('/parameters')} style={{ display: 'block', background: 'rgba(0, 229, 255, 0.05)', color: '#00e5ff', border: '1px solid #00e5ff', padding: '10px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, textAlign: 'center', textDecoration: 'none', cursor: 'pointer', marginBottom: 15 }}>
                  View DFM analysis
                </a>
                <div style={{ fontSize: 12, color: '#8899aa', lineHeight: 1.8 }}>
                  <span style={{ color: '#e0e0e0', fontWeight: 500 }}>{dimsStr} mm</span><br/>
                  ({dimsInStr} in)<br/><br/>
                  <span style={{ color: '#e0e0e0', fontWeight: 500 }}>{vol.toLocaleString()} mm³</span><br/>
                  ({volIn} in³)
                </div>
              </div>

              {/* 中间：参数 */}
              <div style={{ flex: 1, paddingTop: 5 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <input type="checkbox" checked={isSelected} onChange={(e) => handleSelectCard(idx, e.target.checked)} style={{ accentColor: '#00e5ff', width: 18, height: 18, cursor: 'pointer' }} />
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#ffffff', wordBreak: 'break-all', borderLeft: '3px solid #00e5ff', paddingLeft: 10 }}>
                    {part.cadFile?.name || `零件 ${idx + 1}`}
                  </div>
                </div>
                <ul style={{ listStyle: 'none', fontSize: 13, lineHeight: 2.1, margin: 0, padding: 0 }}>
                  <li style={{ display: 'flex', alignItems: 'baseline', color: '#8899aa' }}><span style={{ width: 120, color: '#556', fontSize: 12, flexShrink: 0 }}>Process 工艺:</span> <span style={{ color: '#c0c8d0' }}>{q.quoteResult?.recommended_machine || 'CNC Milling'}</span></li>
                  <li style={{ display: 'flex', alignItems: 'baseline', color: '#8899aa' }}><span style={{ width: 120, color: '#556', fontSize: 12, flexShrink: 0 }}>Material 材料:</span> <span style={{ color: '#c0c8d0' }}>{part.material?.name || '未指定'}</span></li>
                  <li style={{ display: 'flex', alignItems: 'baseline', color: '#8899aa' }}><span style={{ width: 120, color: '#556', fontSize: 12, flexShrink: 0 }}>Surface 表面:</span> <span style={{ color: '#c0c8d0' }}>{part.surfaceTreatments?.length > 0 ? part.surfaceTreatments.map(s=>s.name).join(', ') : 'As machined'} (Ra {part.roughnessRa || 3.2}μm)</span></li>
                  <li style={{ display: 'flex', alignItems: 'baseline', color: '#8899aa' }}><span style={{ width: 120, color: '#556', fontSize: 12, flexShrink: 0 }}>Tolerance 公差:</span> <span style={{ color: '#c0c8d0' }}>{part.toleranceGrade || 'General tolerance: Medium'}</span></li>
                  <li style={{ display: 'flex', alignItems: 'baseline', color: '#8899aa' }}><span style={{ width: 120, color: '#556', fontSize: 12, flexShrink: 0 }}>Threads 螺纹:</span> <span style={{ color: '#c0c8d0' }}>{threadStr}</span></li>
                  
                  <li style={{ color: '#ffcc00', marginTop: 10, background: 'rgba(255, 204, 0, 0.05)', padding: '6px 10px', borderRadius: 4, borderLeft: '2px solid #ffcc00', fontSize: 12, display: 'flex' }}>
                    <span style={{ width: 120, color: '#ffcc00', fontSize: 12, flexShrink: 0 }}>Factory Rule:</span> 
                    <span>Sharp internal corners rounded to 2mm</span>
                  </li>
                </ul>
              </div>

              {/* 右侧：价格与操作 */}
              <div style={{ flex: '0 0 180px', textAlign: 'right', display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: 'auto' }}>
                  <div style={{ fontSize: 13, color: '#8899aa', marginBottom: 5 }}>Qty: {qty}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: '#00e5ff', textShadow: '0 0 15px rgba(0, 229, 255, 0.4)', marginBottom: 4 }}>
                    ¥{basePrice.toFixed(2)}
                  </div>
                  <div style={{ fontSize: 12, color: '#556', marginBottom: 20 }}>¥{unitPrice.toFixed(2)} p/part</div>
                </div>
                
                {/* 实际价校准 */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                  <input type="number" placeholder="实际成交价" value={actualPrices[idx] || ''} onChange={(e) => setActualPrices(prev => ({ ...prev, [idx]: e.target.value }))} style={{ width: '100%', background: 'rgba(0,20,40,0.6)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', padding: '6px 8px', borderRadius: 4, fontSize: 12, outline: 'none' }} />
                  <button onClick={() => handleCalibrate(idx)} disabled={submitting} style={{ background: 'transparent', border: '1px solid #556', color: '#8899aa', padding: '6px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>校准</button>
                </div>
                
                <button onClick={() => openEditModal(idx)} style={{ width: '100%', background: 'transparent', color: '#00e5ff', border: '1px solid #00e5ff', padding: '10px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.3s' }}>
                  ✎ Change specs
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ================= 右侧摘要栏 ================= */}
      <div style={{ position: 'fixed', right: 0, top: 0, bottom: 0, width: 320, background: 'rgba(0,15,35,0.95)', borderLeft: '1px solid rgba(0,229,255,0.15)', padding: '28px 20px', zIndex: 100, overflowY: 'auto' }}>
        <div style={{ color: '#00e5ff', fontSize: 15, fontWeight: 'bold', marginBottom: 15 }}>💵 订单总价 Total Price</div>
        <div style={{ fontSize: 36, fontWeight: 800, color: '#00e5ff', textShadow: '0 0 20px rgba(0,229,255,0.4)', marginBottom: 5 }}>
          ¥{currentTotalPrice.toFixed(2)}
        </div>
        <div style={{ fontSize: 13, color: '#8899aa', marginBottom: 25 }}>包含 {quotes.length} 个零件</div>
        
        <div style={{ color: '#00e5ff', fontSize: 15, fontWeight: 'bold', marginTop: 20, marginBottom: 15 }}>🚚 交期 Lead Time</div>
        
        {['economy', 'standard', 'expedited'].map(type => (
          <div key={type} onClick={() => setSelectedLeadType(type)} style={{ display: 'flex', alignItems: 'center', padding: '14px 0', cursor: 'pointer', borderBottom: '1px solid rgba(0,229,255,0.1)' }}>
            <input type="radio" name="lead" checked={selectedLeadType === type} readOnly style={{ accentColor: '#00e5ff', marginRight: 12, transform: 'scale(1.2)' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#e0e0e0' }}>
                {type === 'economy' ? '经济' : type === 'standard' ? '正常' : '加急'} 
                <span style={{ fontSize: 11, color: '#667', marginLeft: 5 }}>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
              </div>
              <div style={{ fontSize: 12, color: '#8899aa', marginTop: 2 }}>{leadDaysRange[type]}</div>
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#00e5ff' }}>¥{(totalBasePrice * leadMultipliers[type]).toFixed(2)}</div>
          </div>
        ))}
      </div>

      {/* 模拟修改弹窗 */}
      <Modal 
        title={<span style={{ color: '#00e5ff' }}>Change Specifications</span>} 
        open={modalVisible} 
        onOk={() => setModalVisible(false)} 
        onCancel={() => setModalVisible(false)}
        okText="Save"
        cancelText="Cancel"
        styles={{ content: { background: '#00142a', border: '1px solid #00e5ff' }, header: { background: '#00142a', borderBottom: '1px solid rgba(0,229,255,0.2)' } }}
      >
        <p style={{ color: '#8899aa' }}>这里将弹出现有的参数修改表单。若需修改，请点击下方跳转回参数页。</p>
        <Button type="primary" onClick={() => navigate('/parameters')} style={{ background: '#00e5ff', color: '#000', border: 'none', marginTop: 10 }}>返回参数设置页</Button>
      </Modal>

    </div>
  );
}
